import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import BandFilters from './components/BandFilters';
import BandStats from './components/BandStats';
import BandTable from './components/BandTable';
import BandConfigPanel from './components/BandConfigPanel';
import AIRecommendations from './components/AIRecommendations';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { useTenant } from '../../context/TenantContext';
import { useTranslation } from 'react-i18next';
import { useFormatters } from '../../utils/format';
import { buildPayBands } from '../../data/derivedPayBands';

const PayBandManagement = () => {
  const { tenant, isTransitioning } = useTenant();
  const { t } = useTranslation();
  const { num } = useFormatters();
  const [bands, setBands] = useState([]);
  const [filteredBands, setFilteredBands] = useState([]);
  const [selectedBand, setSelectedBand] = useState(null);
  const [isConfigPanelOpen, setIsConfigPanelOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    department: '',
    jobLevel: '',
    status: '',
    currency: ''
  });
  const [isLoading, setIsLoading] = useState(true);



  useEffect(() => {
    setIsLoading(true);
    const built = buildPayBands(tenant);
    const timer = setTimeout(() => {
      setBands(built);
      setFilteredBands(built);
      setIsLoading(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [tenant.id]);

  useEffect(() => {
    // Apply filters and search
    let filtered = bands;

    // Apply search term
    if (searchTerm) {
      filtered = filtered?.filter(band =>
        band?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        band?.department?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        band?.jobLevel?.toLowerCase()?.includes(searchTerm?.toLowerCase())
      );
    }

    // Apply filters
    Object.entries(filters)?.forEach(([key, value]) => {
      if (value) {
        filtered = filtered?.filter(band => band?.[key] === value);
      }
    });

    setFilteredBands(filtered);
  }, [bands, searchTerm, filters]);

  const handleFilterChange = (filterKey, value) => {
    setFilters(prev => ({
      ...prev,
      [filterKey]: value
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      department: '',
      jobLevel: '',
      status: '',
      currency: ''
    });
    setSearchTerm('');
  };

  const handleCreateBand = () => {
    setSelectedBand(null);
    setIsConfigPanelOpen(true);
  };

  const handleEditBand = (band) => {
    setSelectedBand(band);
    setIsConfigPanelOpen(true);
  };

  const handleDeleteBand = (band) => {
    if (window.confirm(`Are you sure you want to delete the "${band?.name}" pay band? This action cannot be undone.`)) {
      setBands(prev => prev?.filter(b => b?.id !== band?.id));
    }
  };

  const handleSaveBand = async (bandData) => {
    try {
      if (selectedBand) {
        // Update existing band
        setBands(prev => prev?.map(band => 
          band?.id === selectedBand?.id 
            ? { ...band, ...bandData, id: selectedBand?.id }
            : band
        ));
      } else {
        // Create new band
        const newBand = {
          ...bandData,
          id: Math.max(...bands?.map(b => b?.id)) + 1,
          employeeCount: 0,
          status: 'draft',
          complianceFlags: []
        };
        setBands(prev => [...prev, newBand]);
      }
      
      setIsConfigPanelOpen(false);
      setSelectedBand(null);
    } catch (error) {
      console.error('Error saving band:', error);
    }
  };

  const handleApplyRecommendation = (recommendation) => {
    console.log('Applying recommendation:', recommendation);
    // Implementation would depend on the specific recommendation type
  };

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/analytics-dashboard' },
    { label: 'Pay Band Management', path: '/pay-band-management' }
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-6 py-8">
            <div className="flex items-center justify-center h-64">
              <div className="flex items-center space-x-3">
                <Icon name="Loader2" size={24} className="animate-spin text-primary" />
                <span className="text-muted-foreground">{t('common.loading')}</span>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <Breadcrumb items={breadcrumbItems} />

          {/* Page Header */}
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-2 gap-3">
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  {t('payBands.title')}
                </span>
                <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-muted text-muted-foreground uppercase tracking-wide">
                  Art. 4 · 6
                </span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight">
                {tenant.name}
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                {t('payBands.subtitle')}
              </p>
            </div>
          </div>

          {/* Tenant context strip */}
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 pb-4 mb-6 border-b border-border text-xs">
            <div className="flex items-center gap-1.5">
              <Icon name="Building2" size={12} className="text-muted-foreground" />
              <span className="text-muted-foreground">{t('payBands.metaIndustry')}</span>
              <span className="font-medium text-foreground">{tenant.industry}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Icon name="Layers" size={12} className="text-muted-foreground" />
              <span className="text-muted-foreground">{t('payBands.metaBands')}</span>
              <span className="font-medium text-foreground tabular-nums">{filteredBands.length}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Icon name="Users" size={12} className="text-muted-foreground" />
              <span className="text-muted-foreground">{t('payBands.metaWorkers')}</span>
              <span className="font-medium text-foreground tabular-nums">
                {num(tenant.employees)}
              </span>
            </div>
            <div className="flex items-center gap-1.5 ml-auto">
              <span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" />
              <span className="text-muted-foreground">{t('dashboard.meta.hrisSync')}</span>
              <span className="font-medium text-foreground">{t('common.live')}</span>
            </div>
          </div>

          <div className={`transition-opacity duration-300 ${isTransitioning ? 'opacity-40' : 'opacity-100'}`}>
          {/* AI Recommendations */}
          <AIRecommendations onApplyRecommendation={handleApplyRecommendation} />
          
          {/* Statistics */}
          <BandStats bands={filteredBands} />
          
          {/* Filters */}
          <BandFilters
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            onCreateBand={handleCreateBand}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
          />

          {/* Main Content Area */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Band Table */}
            <div className={`${isConfigPanelOpen ? 'lg:col-span-2' : 'lg:col-span-3'} transition-layout`}>
              <BandTable
                bands={filteredBands}
                onSelectBand={setSelectedBand}
                selectedBand={selectedBand}
                onEditBand={handleEditBand}
                onDeleteBand={handleDeleteBand}
              />
            </div>

            {/* Configuration Panel */}
            {isConfigPanelOpen && (
              <div className="lg:col-span-1">
                <BandConfigPanel
                  band={selectedBand}
                  onSave={handleSaveBand}
                  onClose={() => {
                    setIsConfigPanelOpen(false);
                    setSelectedBand(null);
                  }}
                  isVisible={isConfigPanelOpen}
                />
              </div>
            )}
          </div>

          {/* Empty State */}
          {filteredBands?.length === 0 && !isLoading && (
            <div className="text-center py-12">
              <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">No Pay Bands Found</h3>
              <p className="text-muted-foreground mb-4">
                {searchTerm || Object.values(filters)?.some(v => v) 
                  ? 'Try adjusting your search or filters to find what you\'re looking for.'
                  : 'Get started by creating your first pay band.'
                }
              </p>
              {!searchTerm && !Object.values(filters)?.some(v => v) && (
                <Button 
                  variant="default"
                  iconName="Plus"
                  iconPosition="left"
                  onClick={handleCreateBand}
                >
                  Create First Pay Band
                </Button>
              )}
            </div>
          )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default PayBandManagement;